import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash-es';
import { forkJoin, Observable } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import { PHONE_NUMBER_OPTIONS } from 'src/app/core/constants/default-text-mask-options';
import {
  CHALLENGE_QUESTIONS,
  PASSWORD_PATTERN_ERROR,
  RegistrationFormFieldConfig,
  TIME_ZONES
} from 'src/app/core/constants/forms';
import { USER_REGISTRATION_FORM_FIELDS } from 'src/app/core/constants/mapping-keys';
import { PASSWORD_PATTERN, PHONE_NUMBER_PATTERN } from 'src/app/core/constants/regex';
import { SUD_SMI_AUTOMATIC_ACCESS_MESSAGE } from 'src/app/core/constants/user-management';
import { ReferenceTable } from 'src/app/core/models/reference-table';
import { User } from 'src/app/core/models/user';
import { UserEmployer } from 'src/app/core/models/user-employer';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { UserService } from 'src/app/core/services/user.service';
import { isNotEmpty } from 'src/app/core/utils/object-util';
import { AffiliationBase } from '../users/shared/affiliation-base';

@Component({
  selector: 'th-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss']
})
export class EditProfileComponent extends AffiliationBase implements OnInit {
  @Input() editAuthenticatedUser = false;
  @Input() userId: number;
  @Output() userUpdated = new EventEmitter<User>();

  @ViewChild('form', { static: true }) ngForm: NgForm;

  phoneNumberMaskOptions: string = PHONE_NUMBER_OPTIONS;
  phoneNumberPattern: string = PHONE_NUMBER_PATTERN;
  passwordPattern: string = PASSWORD_PATTERN;
  passwordPatternError: string = PASSWORD_PATTERN_ERROR;
  sudSmiAutomaticAccessMessage = SUD_SMI_AUTOMATIC_ACCESS_MESSAGE;
  submitting = false;
  timeZones = TIME_ZONES;
  challengeQuestions = CHALLENGE_QUESTIONS;
  passPhraseMaxLength = 15;
  formFieldsEnabled: RegistrationFormFieldConfig = new RegistrationFormFieldConfig();
  loading = false;
  hasUpdateUserNamePermission: boolean;
  hasUpdateUserPasswordPermission: boolean;

  constructor(
    private userService: UserService,
    private organizationService: OrganizationService,
    private authService: AuthService,
    public modalService: NgbModal,
    protected cdr: ChangeDetectorRef
  ) {
    super(cdr);
  }

  ngOnInit() {
    let userId: number;

    if (this.editAuthenticatedUser) {
      userId = this.authService.user.userId;
    } else {
      userId = this.userId;
    }

    this.hasUpdateUserNamePermission = this.authService.isEntitled('/users/updateUserName');
    this.hasUpdateUserPasswordPermission = this.authService.isEntitled('/users/updateUserPassword');
    this.loading = true;

    this.userService
      .getUser(userId, false)
      .pipe(
        concatMap((user) => {
          this.user = cloneDeep(user);

          if (this.user.employerId === 0) {
            this.user.employerId = null;
          }

          this.applyEquivalentTimezoneForDropdown();

          this.user.sudSmiProgramDashboardAccess = this.isCurrentRoleExternalAgencyAndJobTitleCMHCBHCM;

          const getJobTitles = this.userService.getJobTitles(this.user.orgId, this.user.orgLevel);
          const getUserEmployers = this.userService.getUserEmployers(this.user.orgId, this.user.orgLevel);

          return forkJoin([getJobTitles, getUserEmployers]);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe(([jobTitles, employers]: [ReferenceTable[], UserEmployer[]]) => {
        this.jobTitles = jobTitles;
        this.employers = employers;

        this.checkMissingEmployer();
      });

    this.getMappingKeys();
  }

  handleUpdatedUser(user: User) {
    this.userUpdated.emit(user);
    this.modalService.dismissAll();
  }

  onSubmit() {
    this.submitting = true;

    this.determineWhichObservable()
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe((user) => this.handleUpdatedUser(user));
  }

  determineWhichObservable(): Observable<User> {
    this.employers.forEach((employer) => {
      if (employer.id === this.user.employerId) {
        this.user.employer = employer.description;
      }
    });
    return this.editAuthenticatedUser
      ? this.userService.updateAuthenticatedUser(this.user)
      : this.userService.updateUser(this.user);
  }

  getMappingKeys() {
    const userRegistrationFormFields = this.organizationService.getCachedMappingKey(USER_REGISTRATION_FORM_FIELDS);

    if (isNotEmpty(userRegistrationFormFields)) {
      this.formFieldsEnabled = JSON.parse(userRegistrationFormFields);
    }
  }

  private checkMissingEmployer() {
    this.user.employerId = this.employers.find((employer) => employer.id === this.user.employerId)?.id || null;
  }

  /**
   * The values for this dropdown differ from what we have in the database due
   * to using CST/CDT, EST/EDT, etc. as if they are equivalent.
   *
   * TODO: Remove this once we use proper timezones
   * (https://en.wikipedia.org/wiki/List_of_tz_database_time_zones) in the DB
   * e.g. switch CDT, CST to US/Central and EDT, EST to US/Eastern
   */
  private applyEquivalentTimezoneForDropdown() {
    switch (this.user.timezone) {
      case 'CST':
        this.user.timezone = 'CDT';
        break;
      case 'EST':
        this.user.timezone = 'EDT';
        break;
      case 'MST':
        this.user.timezone = 'MDT';
        break;
      case 'PST':
        this.user.timezone = 'PDT';
        break;
    }
  }
}
